import type {ServerDateValue} from '../../api/columns/contracts/date'
import type {Iteration, IterationValue} from '../../api/columns/contracts/iteration'
import {MemexColumnDataType, SystemColumnId} from '../../api/columns/contracts/memex-column'
import type {NumericValue} from '../../api/columns/contracts/number'
import type {PersistedOption, SingleSelectValue} from '../../api/columns/contracts/single-select'
import {
  type MemexColumnData,
  type MemexColumnDataValue,
  type RemoteUpdatePayload,
  SystemIdToDataTypeMap,
} from '../../api/columns/contracts/storage'
import type {EnrichedText} from '../../api/columns/contracts/text'
import type {ExtendedRepository, IssueType, Label, LinkedPullRequest, Milestone, User} from '../../api/common-contracts'
import type {UpdateItemContext} from '../../api/memex/contracts'
import type {
  IssueContent,
  ItemContent,
  MemexItem,
  MemexItemUpdateData,
  PullRequestContent,
  UpdateMemexItemResponseData,
} from '../../api/memex-items/contracts'
import {ItemType} from '../../api/memex-items/item-type'
import type {ItemCompletion} from '../../api/side-panel/contracts'
import {assertNever} from '../../helpers/assert-never'
import {getAllIterationsForConfiguration} from '../../helpers/iterations'
import {isNumber, parseColumnId} from '../../helpers/parsing'
import {isDraftIssueColumn} from '../../models/column-capabilities'
import type {ColumnModel} from '../../models/column-model'
import type {MemexItemModel} from '../../models/memex-item-model'
import {getCurrentColumnValueFromModel} from '../column-values/column-value'

function isUserDefinedColumn(memexProjectColumnId: number | SystemColumnId): memexProjectColumnId is number {
  return isNumber(memexProjectColumnId)
}

/**
 * Draft issues do not support all column types (e.g. Milestones)
 *
 * @param memexProjectColumnId    - the column id to update
 * @returns whether the item column is a valid draft issue column or not
 */
export function isValidDraftItemColumn(memexProjectColumnId: number | SystemColumnId) {
  if (isUserDefinedColumn(memexProjectColumnId)) {
    return true
  }

  return isDraftIssueColumn(SystemIdToDataTypeMap[memexProjectColumnId])
}

export function sidebarFieldsToDisplay(contentType: ItemType, columns: Array<ColumnModel>) {
  switch (contentType) {
    case ItemType.DraftIssue:
      return columns.filter(
        column => column.id !== SystemColumnId.Title && isValidDraftItemColumn(column.id) && !column.isIssueField,
      )
    case ItemType.Issue:
      return columns.filter(column => column.id !== SystemColumnId.Title && column.id !== SystemColumnId.Reviewers)
    default:
      return []
  }
}

/**
 * Not all issues support all possible content fields (e.g. RedactedItems)
 *
 * @param content - the content of the item to validate
 * @returns whether the content is valid for an issue or pull request or not
 */
export function isIssueOrPullRequestContent(content: ItemContent): content is IssueContent | PullRequestContent {
  return content && 'id' in content && 'url' in content
}

export function buildMemexItemUpdateData(
  contentType: ItemType,
  memexProjectColumnValues: Array<RemoteUpdatePayload> | undefined,
  previousMemexProjectItemId: number | '' | undefined,
  {layoutType, parentIssueId}: UpdateItemContext = {},
): MemexItemUpdateData | undefined {
  if (memexProjectColumnValues && contentType === ItemType.DraftIssue) {
    memexProjectColumnValues = memexProjectColumnValues.filter(item =>
      isValidDraftItemColumn(item.memexProjectColumnId),
    )
  }

  /**
   * When no fields should update, we don't want to send an empty payload.
   */
  if ((!memexProjectColumnValues || memexProjectColumnValues.length === 0) && previousMemexProjectItemId == null) {
    return undefined
  }
  return {
    previousMemexProjectItemId,
    memexProjectColumnValues,
    layoutType,
    parentIssueId,
  } as MemexItemUpdateData
}

/**
 * Use to convert ColumnData from a MemexItemModel to array of MemexColumnData
 *
 * @param model - The MemexItemModel to convert to MemexColumnData
 * @returns An array of MemexColumnData
 */
export function convertToMemexColumnData(model: MemexItemModel): Array<MemexColumnData> {
  return Object.keys(model.columns).reduce<Array<MemexColumnData>>((acc, id) => {
    const columnId = parseColumnId(id)

    if (!columnId) {
      return acc
    }

    const columnValue = getCurrentColumnValueFromModel(columnId, model)

    if (!columnValue) {
      return acc
    }

    return [...acc, columnValue]
  }, [])
}

/**
 *
 * @param field - The field to get the value for
 * @param itemColumnValueMap - The map of column id to column value
 * @returns
 */
export function getMemexItemContentForField(
  field: ColumnModel,
  itemColumnValueMap: Map<number | SystemColumnId, MemexColumnDataValue>,
  item: MemexItemModel,
):
  | Array<User>
  | EnrichedText
  | Iteration
  | PersistedOption
  | Array<Label>
  | Milestone
  | Array<LinkedPullRequest>
  | ServerDateValue
  | NumericValue
  | ExtendedRepository
  | ItemCompletion
  | IssueType
  | undefined {
  const itemColumnValue = itemColumnValueMap.get(field.id)
  switch (field.dataType) {
    case MemexColumnDataType.Assignees: {
      // Will fetch assignees from item store is api response isn't available yet
      const assignees = itemColumnValue ? (itemColumnValue as Array<User>) : item.getAssignees()
      return assignees
    }
    case MemexColumnDataType.Labels: {
      // Will fetch labels from item store is api response isn't available yet
      const labels = itemColumnValue ? (itemColumnValue as Array<Label>) : item.getLabels()
      return labels
    }
    case MemexColumnDataType.Iteration: {
      // since this field is specific to memex items, we can return undefined until have a valid response
      const selectedIterationOptionId = itemColumnValue
        ? (itemColumnValue as IterationValue)
        : (item.getCustomField(field.id) as IterationValue)
      if (!selectedIterationOptionId) return undefined
      const iterationOptions = field.settings.configuration
        ? getAllIterationsForConfiguration(field.settings.configuration)
        : []
      const selectedIterationOption = iterationOptions.find(option => option.id === selectedIterationOptionId.id)
      return selectedIterationOption
    }
    case MemexColumnDataType.SingleSelect: {
      // since this field is specific to memex items, we can return undefined until we have a valid response
      const singleSelectValue = itemColumnValue
        ? (itemColumnValue as SingleSelectValue)
        : field.id === SystemColumnId.Status
          ? item.getStatus()
          : item.getCustomField<SingleSelectValue>(field.id)
      if (!singleSelectValue) return undefined
      const selectedOption = field.settings?.options?.find(option => option.id === singleSelectValue.id)
      return selectedOption
    }
    case MemexColumnDataType.Text: {
      // since this field is specific to memex items, we can return undefined until have a valid response
      const textValue = itemColumnValue
        ? (itemColumnValue as EnrichedText)
        : (item.getCustomField(field.id) as EnrichedText)
      return textValue
    }
    case MemexColumnDataType.Milestone: {
      const milestone = itemColumnValue ? (itemColumnValue as Milestone) : item.getMilestone()
      return milestone
    }
    case MemexColumnDataType.LinkedPullRequests: {
      const linkedPullRequest = itemColumnValue
        ? (itemColumnValue as Array<LinkedPullRequest>)
        : item.getLinkedPullRequests()
      return linkedPullRequest
    }
    case MemexColumnDataType.Date: {
      const dateValue = itemColumnValue
        ? (itemColumnValue as ServerDateValue)
        : (item.getCustomField(field.id) as ServerDateValue)
      return dateValue
    }
    case MemexColumnDataType.Number: {
      const numberValue = itemColumnValue
        ? (itemColumnValue as NumericValue)
        : (item.getCustomField(field.id) as NumericValue)
      return numberValue
    }
    case MemexColumnDataType.Repository: {
      const repository = itemColumnValue ? (itemColumnValue as ExtendedRepository) : item.getExtendedRepository()
      return repository
    }
    case MemexColumnDataType.IssueType: {
      const type = itemColumnValue ? (itemColumnValue as IssueType) : item.getIssueType()
      return type
    }
    default:
      break
  }
}

/**
 * Use to merge the previous state of an item with updated data from a server request
 *
 * @param originalModel - The previous state of the item
 * @param itemUpdate  - The next state of the item given a live update, local update, or the updated item data from a server request
 * @returns the next state of the item merged with the previous state
 */
export function mergeItemData(
  originalModel: MemexItemModel,
  itemUpdate: MemexItem | UpdateMemexItemResponseData | MemexItemModel,
): MemexItem | null {
  const {
    content,
    contentType,
    id,
    priority,
    virtualPriority,
    updatedAt,
    createdAt,
    issueCreatedAt,
    issueClosedAt,
    state,
    stateReason,
    blockedByCount,
    agentTaskState,
    agentTaskTitle,
    agentTaskCreatedAt,
    agentTaskCompletedAt,
    agentTaskAgentId,
  } = itemUpdate

  let columnValues = convertToMemexColumnData(originalModel)
  if ('columnData' in itemUpdate) {
    // If we're here, we're merging with another MemexItemModel, and we should prefer `columnData`
    // over `memexProjectColumnValues` as `columnData` contains any client-side optimistic updates,
    // but `memexProjectColumnValues` does not.
    columnValues = convertToMemexColumnData(itemUpdate)
  } else if (itemUpdate.memexProjectColumnValues) {
    // Otherwise, if `memexProjectColumnValues` is present, use that.
    columnValues = itemUpdate.memexProjectColumnValues
  }
  let contentRepositoryId = originalModel.contentRepositoryId ?? -1
  // If the item has a repository ID, then copy it. This fixes the case where the
  // old item is a draft issue, and the new item is an issue as the result of conversion.
  if ('contentRepositoryId' in itemUpdate && itemUpdate.contentRepositoryId != null) {
    contentRepositoryId = itemUpdate.contentRepositoryId
  }
  const newContent = content ?? originalModel.content
  const prevContent = originalModel.content

  switch (contentType) {
    case ItemType.Issue:
    case ItemType.PullRequest: {
      if (!isIssueOrPullRequestContent(newContent)) {
        return null
      }

      return {
        contentType,
        content: {...prevContent, ...newContent},
        contentRepositoryId,
        memexProjectColumnValues: columnValues,
        id,
        priority,
        virtualPriority,
        updatedAt,
        createdAt,
        issueCreatedAt,
        issueClosedAt,
        state,
        stateReason,
        blockedByCount,
        agentTaskState: agentTaskState ?? originalModel.agentTaskState,
        agentTaskTitle: agentTaskTitle ?? originalModel.agentTaskTitle,
        agentTaskCreatedAt: agentTaskCreatedAt ?? originalModel.agentTaskCreatedAt,
        agentTaskCompletedAt: agentTaskCompletedAt ?? originalModel.agentTaskCompletedAt,
        agentTaskAgentId: agentTaskAgentId ?? originalModel.agentTaskAgentId,
      }
    }
    case ItemType.DraftIssue:
      return {
        contentType,
        content: {...prevContent, ...newContent},
        memexProjectColumnValues: columnValues,
        id,
        priority,
        virtualPriority,
        updatedAt,
        createdAt,
      }
    case ItemType.RedactedItem:
      return {
        contentType,
        content: newContent,
        memexProjectColumnValues: columnValues,
        id,
        priority,
        virtualPriority,
        updatedAt,
        createdAt,
      }
    default: {
      assertNever(contentType)
    }
  }
}
